import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["checkbox", "totalCount", "selectedCount", "bulkActionContainer", "filter", "filterForm", "exportButton"]

  connect() {
    this.selectedItems = new Set();
    this.allSelected = false;

    this.boundUpdateCheckboxes = this.updateCheckboxes.bind(this);

    document.addEventListener("turbo:load", this.boundUpdateCheckboxes);

    // We use this instead of a Stimulus value because the table exists outside of a turbo-frame that gets refreshed with
    // new results.
    this.totalCount = parseInt(this.totalCountTarget.dataset.totalCountValue);

    this.element.querySelector("turbo-frame[id='resultslist']").addEventListener("turbo:frame-render", () => {
      this.totalCount = parseInt(this.totalCountTarget.dataset.totalCountValue);
    });

    if (this.hasBulkActionContainerTarget) {
      this.updateCheckboxes();
    }
  }

  disconnect() {
    document.removeEventListener("turbo:load", this.boundUpdateCheckboxes);
  }

  updateCheckboxes() {
    if (this.selectedItems.size > 0 || this.allSelected) {
      this.totalCountTarget.classList.add("hidden");
      this.bulkActionContainerTarget.classList.remove("hidden");
      this.selectedCountTarget.textContent = this.allSelected ? (this.totalCount - this.selectedItems.size) + " selected" : this.selectedItems.size + " selected";

      if (this.hasButtonExportTarget) {
        this.exportButtonTarget.classList.add("hidden");
      }
    } else {
      this.totalCountTarget.classList.remove("hidden");
      this.bulkActionContainerTarget.classList.add("hidden");
      if (this.hasButtonExportTarget) {
        this.exportButtonTarget.classList.remove("hidden");
      }
    }

    this.checkboxTargets.forEach(checkbox => {
      if (this.allSelected) {
        checkbox.checked = !this.selectedItems.has(checkbox.dataset["itemIdParam"]);
      } else {
        checkbox.checked = this.selectedItems.has(checkbox.dataset["itemIdParam"]);
      }
    });
  }

  toggle(event) {
    const itemId = event.target.dataset["itemIdParam"];
    if (this.selectedItems.has(itemId)) {
      this.selectedItems.delete(itemId);
    } else {
      this.selectedItems.add(itemId);
    }
    this.updateCheckboxes();
  }

  toggleSelectAll(event) {
    this.selectedItems.clear();
    this.allSelected = !this.allSelected;
    this.updateCheckboxes();
  }

  bulkAction(event) {
    event.preventDefault();
    event.stopPropagation();

    const url = new URL("/dashboard/shared/background_actions/new", window.location.origin);

    url.searchParams.set("background_action[type]", event.params.action);
    url.searchParams.set("background_action[filters]", this.currentFiltersAsJson());

    if (this.allSelected) {
      url.searchParams.set("background_action[include_all]", "true");
      this.selectedItems.forEach(item => {
        url.searchParams.append("background_action[exclude_ids][]", item);
      });
    } else {
      this.selectedItems.forEach(item => {
        url.searchParams.append("background_action[include_ids][]", item);
      });
    }

    window.renderModal(url.toString());
  }

  currentFiltersAsJson() {
    const filters = {};
    if (this.hasFilterFormTarget) {
      for (const pair of new FormData(this.filterFormTarget).entries()) {
        if (pair[0].endsWith("[]")) {
          filters[pair[0].slice(0, -2)] ||= [];
          filters[pair[0].slice(0, -2)].push(pair[1]);
        } else {
          filters[pair[0]] = pair[1];
        }
      }
    }

    return JSON.stringify(filters);
  }

  addFilter(event) {
    const filterInput = event.target.dataset["filterInputParam"];
    const form = event.target.closest("form");
    const input = document.createElement("input");
    input.setAttribute("type", "hidden");
    input.setAttribute("name", filterInput);
    input.setAttribute("value", "");
    form.appendChild(input);
    form.requestSubmit();
    const dropdown = this.application.getControllerForElementAndIdentifier(event.target.closest("[data-controller='dropdown']"), "dropdown");
    dropdown.close();
  }

  removeFilter(event) {
    // The actual changing of the filter happens in filter_controller.js,
    // we just need to reset the selected items and update the checkboxes.
    this.clearSelections();
  }

  clearSelections() {
    this.selectedItems.clear();
    this.allSelected = false;
    this.updateCheckboxes();
  }
}